import { GetServerSidePropsContext, NextPageContext } from 'next';
import { COOKIES_CONFIG } from '@belong/common';
import { parseCookies } from 'nookies';
import { ApiClient, getJwtToken } from 'src/utils/api-client';
import {
  buildApiClient,
  buildApiClientHelpers,
  buildOctaneClient,
  fetcher,
  type RequestInitWithJsonContent,
} from 'src/utils/build-api-client';
import {
  configuredApiUrl,
  PLATFORM_COOKIE_NAME,
} from 'src/features/member-view/server-config';
import {
  isMemberViewPage,
  MEMBER_VIEW_PAGE_COOKIE,
} from 'src/features/member-view/mode';

const { TRACKING_ID, JWT_TOKEN } = COOKIES_CONFIG;

function memberViewServerClient(
  ctx: NextPageContext | GetServerSidePropsContext | undefined,
  service: 'platform' | 'octane',
): ApiClient | null {
  const path = ctx && 'resolvedUrl' in ctx ? ctx.resolvedUrl : ctx?.req?.url;
  const token = parseCookies(ctx)[MEMBER_VIEW_PAGE_COOKIE];
  if (!token && (!path || !isMemberViewPage(path.split('?')[0] || '')))
    return null;
  if (!token || token.length >= 8192)
    throw new Error('Member view session required');
  const base = configuredApiUrl(service);
  if (!base) throw new Error('Member view unavailable');

  return buildApiClientHelpers(
    <T>(endpoint: string, config: RequestInitWithJsonContent): Promise<T> => {
      if (config.method !== 'GET')
        return Promise.reject(new Error('Member view is read only.'));
      if (!endpoint.startsWith('/') || endpoint.startsWith('//'))
        return Promise.reject(new Error('Invalid member view request.'));
      const upstream = new URL(endpoint.slice(1), base);
      return fetcher<T>('', upstream.href, {
        method: 'GET',
        cache: 'no-store',
        redirect: 'manual',
        signal: AbortSignal.timeout(15000),
        headers:
          service === 'platform'
            ? { Cookie: `${PLATFORM_COOKIE_NAME}=${token}` }
            : { Authorization: `Bearer ${token}` },
      });
    },
  );
}

export function buildServerClient(
  ctx?: NextPageContext | GetServerSidePropsContext,
  jwtOverride?: string,
): ApiClient {
  const memberClient = memberViewServerClient(ctx, 'platform');
  if (memberClient) return memberClient;
  const cookies = parseCookies(ctx);

  const trackingId = cookies[TRACKING_ID.name];
  let jwtToken = cookies[JWT_TOKEN.name] || getJwtToken();

  if (jwtOverride) {
    jwtToken = jwtOverride;
  }

  const apiClient = buildApiClient(jwtToken, trackingId);

  return apiClient;
}

export function buildOctaneServerClient(
  ctx?: NextPageContext | GetServerSidePropsContext,
  jwtOverride?: string,
): ApiClient {
  const memberClient = memberViewServerClient(ctx, 'octane');
  if (memberClient) return memberClient;
  const cookies = parseCookies(ctx);

  const trackingId = cookies[TRACKING_ID.name];
  let jwtToken = cookies[JWT_TOKEN.name] || getJwtToken();

  if (jwtOverride) {
    jwtToken = jwtOverride;
  }

  return buildOctaneClient(jwtToken, trackingId);
}
